/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import {
  PaystubItem,
  YEAR_OPTIONS,
  MONTH_OPTIONS,
  findAllPaystubs,
  syncPaystubs,
  fetchHindiPdf,
  downloadBase64Pdf,
  base64ToObjectUrl,
  getLoggedInEmpId,
} from "../salaryStatement/salaryStatement.service";

const SalaryStatementHindi: React.FC = () => {
  const empId = useMemo(() => getLoggedInEmpId(), []);

  const [selectedYear, setSelectedYear] = useState<string>("2025");
  const [selectedMonthName, setSelectedMonthName] = useState<string>("March");

  const [paystubs, setPaystubs] = useState<PaystubItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [downloadingSeq, setDownloadingSeq] = useState<string | null>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>("");

  const monthNumber = MONTH_OPTIONS.indexOf(selectedMonthName) + 1;

  const fetchPaystubs = useCallback(async () => {
    const year = parseInt(selectedYear, 10);
    const month = MONTH_OPTIONS.indexOf(selectedMonthName) + 1;
    if (!year || month < 1) return;
    setLoading(true);
    try {
      const list = await findAllPaystubs({ year, month, empId });
      setPaystubs(list);
    } catch (err: any) {
      setPaystubs([]);
      toast.error(err?.message || "Failed to load salary statement");
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonthName, empId]);

  useEffect(() => {
    fetchPaystubs();
  }, [fetchPaystubs]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleSync = useCallback(async () => {
    if (!empId) {
      toast.error("Employee ID not found");
      return;
    }
    setSyncing(true);
    try {
      await syncPaystubs(empId);
      toast.success("Sync completed");
      await fetchPaystubs();
    } catch (err: any) {
      toast.error(err?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  }, [empId, fetchPaystubs]);

  const handleDownload = useCallback(
    async (item: PaystubItem) => {
      const seq = String(
        item.sequenceNumber ?? item.SEQUENCENUMBER ?? item.id ?? ""
      );
      if (!seq || !empId) {
        toast.error("Cannot download: missing sequence or employee ID");
        return;
      }
      setDownloadingSeq(seq);
      try {
        const result = await fetchHindiPdf({
          empId,
          year: parseInt(selectedYear, 10),
          month: monthNumber,
          monthName: selectedMonthName,
          sequenceNumber: seq,
        });
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(base64ToObjectUrl(result.base64));
        setPreviewName(result.fileName);
        downloadBase64Pdf(result.base64, result.fileName);
        toast.success("Downloaded");
      } catch (err: any) {
        toast.error(err?.message || "Download failed");
      } finally {
        setDownloadingSeq(null);
      }
    },
    [empId, selectedYear, monthNumber, selectedMonthName, previewUrl]
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">
                  वेतन विवरण (हिंदी) / Salary Statement (Hindi)
                </h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Other Services</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Salary Statement (Hindi)
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <button
                type="button"
                className="btn btn-outline-primary d-inline-flex align-items-center"
                onClick={handleSync}
                disabled={syncing}
                title="Sync salary statements"
              >
                {syncing ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                ) : (
                  <i className="ti ti-refresh me-2" />
                )}
                Sync
              </button>
            </li>
          </ul>
        </div>

        {/* Filter bar */}
        <div className="card mb-3">
          <div className="card-body py-3">
            <div className="row g-3 align-items-end">
              <div className="col-sm-6 col-md-4 col-lg-3">
                <label className="form-label fw-medium mb-1">वर्ष / Year</label>
                <select
                  className="form-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6 col-md-4 col-lg-3">
                <label className="form-label fw-medium mb-1">माह / Month</label>
                <select
                  className="form-select"
                  value={selectedMonthName}
                  onChange={(e) => setSelectedMonthName(e.target.value)}
                >
                  {MONTH_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">वेतन विवरण / Salary Statement</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mt-3 mb-0">
                  लोड हो रहा है... / Loading salary statement...
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered datanew">
                  <thead>
                    <tr>
                      <th>भुगतान दिनांक / Payment Date</th>
                      <th>पेरोल प्रकार / Payroll Type</th>
                      <th className="text-end">कुल राशि / Gross Amount</th>
                      <th className="text-end">भुगतान राशि / Payment Amount</th>
                      <th className="text-center">कार्रवाई / Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paystubs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                          {selectedMonthName} {selectedYear} के लिए कोई वेतन
                          रिकॉर्ड नहीं / No salary records.
                        </td>
                      </tr>
                    ) : (
                      paystubs.map((item, index) => {
                        const seq = String(
                          item.sequenceNumber ??
                            item.SEQUENCENUMBER ??
                            item.id ??
                            index
                        );
                        const isDownloading = downloadingSeq === seq;
                        return (
                          <tr key={seq}>
                            <td>{item.paymentDate || "-"}</td>
                            <td>{item.type || "-"}</td>
                            <td className="text-end">
                              {item.grossAmount || "-"}
                            </td>
                            <td className="text-end">
                              {item.paymentAmount || "-"}
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-success d-inline-flex align-items-center gap-1"
                                onClick={() => handleDownload(item)}
                                disabled={isDownloading}
                                title="डाउनलोड PDF / Download PDF"
                              >
                                {isDownloading ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                  />
                                ) : (
                                  <i className="ti ti-download" />
                                )}
                                <span>डाउनलोड / Download</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* PDF preview of the actually downloaded statement */}
        {previewUrl && (
          <div className="card mt-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                वेतन विवरण PDF / Salary Statement PDF
              </h5>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => window.open(previewUrl, "_blank")}
                  title="नए टैब में खोलें / Open in new tab"
                >
                  <i className="ti ti-external-link me-1" />
                  Open
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    URL.revokeObjectURL(previewUrl);
                    setPreviewUrl(null);
                  }}
                  title="Hide PDF"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              <div style={{ width: "100%", height: "800px" }}>
                <iframe
                  src={`${previewUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                  title={previewName || "वेतन विवरण PDF / Salary Statement PDF"}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalaryStatementHindi;
